import { useState, useEffect, useRef, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import api from '../../../services/api';

interface Story {
  id: number;
  label: string;
  image: string;
  border_color: string | null;
  is_new: boolean;
  link_type: string | null;
  link_value: string | null;
  popup_content: string | null;
  visible: boolean;
  order: number;
}

const emptyForm = {
  label: '', image: '', border_color: '#A78BFA', is_new: false,
  link_type: '', link_value: '', popup_content: '', visible: true, order: 0,
};

export default function StoriesPage() {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editStory, setEditStory] = useState<Story | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState('');

  const processFile = useCallback((file: File) => {
    if (!file.type.match(/image\/(jpeg|png|webp)/)) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((f) => ({ ...f, image: ev.target?.result as string }));
      setPreview(ev.target?.result as string);
      setUploading(false);
    };
    reader.readAsDataURL(file);
  }, []);

  const load = () => {
    setLoading(true);
    api.get('/stories/all').then(({ data }) => { setStories(data.data || data); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditStory(null); setForm(emptyForm); setShowModal(true); };

  const openEdit = (s: Story) => {
    setEditStory(s);
    setForm({
      label: s.label, image: s.image, border_color: s.border_color || '#A78BFA',
      is_new: s.is_new, link_type: s.link_type || '', link_value: s.link_value || '',
      popup_content: s.popup_content || '', visible: s.visible, order: s.order,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const payload = { ...form, border_color: form.border_color || '#A78BFA' };
    if (editStory) {
      await api.put(`/stories/${editStory.id}`, payload);
    } else {
      await api.post('/stories', payload);
    }
    setShowModal(false);
    load();
  };

  const confirmDelete = async () => {
    if (deleteConfirm) { await api.delete(`/stories/${deleteConfirm}`); setDeleteConfirm(null); load(); }
  };

  const inputClass = "w-full px-3 py-2 text-xs rounded-xl border-2 border-rose-100 bg-rose-50/30 focus:outline-none focus:border-rose-300 transition-all";

  return (
    <AdminLayout title="סטוריז" subtitle="ניהול סיפורים ותמונות בראש הדף">
      <div className="flex justify-end mb-4">
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap">
          <i className="ri-add-line text-base"></i> הוסף סטורי
        </button>
      </div>

      {loading ? <div className="flex justify-center py-20"><p className="text-gray-500">טוען...</p></div> : (
        <div className="bg-white rounded-2xl border-2 border-rose-100 overflow-hidden overflow-x-auto">
          <div className="grid grid-cols-[3fr_2fr_1fr_1fr_1fr_1.5fr_auto] gap-0 border-b-2 border-rose-100 bg-rose-50/60 px-4 py-3 min-w-[800px]">
            {['תווית', 'תמונה', 'סוג קישור', 'חדש', 'סטטוס', 'תוכן פופ-אפ', ''].map((h) => (
              <span key={h} className="text-xs font-bold text-gray-500 px-2">{h}</span>
            ))}
          </div>
          <div className="divide-y-2 divide-rose-50">
            {stories.map((s) => (
              <div key={s.id} className="grid grid-cols-[3fr_2fr_1fr_1fr_1fr_1.5fr_auto] gap-0 px-4 py-3 items-center hover:bg-rose-50/30 transition-colors min-w-[800px]">
                <div className="flex items-center gap-2.5 px-2">
                  <div className="w-8 h-8 rounded-lg border-2 border-rose-100 overflow-hidden flex-shrink-0">
                    <img src={s.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-xs font-semibold text-gray-800 truncate">{s.label}</p>
                </div>
                <div className="px-2"><span className="text-xs text-gray-500 truncate block max-w-[120px]">{s.image?.slice(0, 40)}...</span></div>
                <div className="px-2">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap bg-violet-50 text-violet-600 border-violet-200">{s.link_type || 'popup'}</span>
                </div>
                <div className="px-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${s.is_new ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>{s.is_new ? 'כן' : 'לא'}</span>
                </div>
                <div className="px-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${s.visible ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>{s.visible ? 'מוצג' : 'מוסתר'}</span>
                </div>
                <div className="px-2"><span className="text-xs text-gray-500 truncate block max-w-[100px]">{s.popup_content?.slice(0, 25) || '—'}</span></div>
                <div className="px-2 flex gap-1">
                  <button onClick={() => openEdit(s)} className="px-2 py-1 rounded-lg border-2 border-rose-200 bg-rose-50 text-rose-500 text-xs font-semibold hover:bg-rose-100 cursor-pointer whitespace-nowrap">ערוך</button>
                  <button onClick={() => setDeleteConfirm(s.id)} className="px-2 py-1 rounded-lg border-2 border-red-200 bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 cursor-pointer whitespace-nowrap">מחק</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4" dir="rtl">
          <div className="bg-white rounded-2xl border-2 border-rose-100 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-800 text-base">{editStory ? 'עריכת סטורי' : 'הוספת סטורי חדש'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-400 hover:bg-gray-50 cursor-pointer"><i className="ri-close-line text-base"></i></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-xs font-semibold text-gray-600 mb-1">תווית</label><input type="text" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} className={inputClass} /></div>
              <div><label className="block text-xs font-semibold text-gray-600 mb-1">תמונה</label>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); }} className="hidden" />
                {!form.image ? (
                  <div onClick={() => fileRef.current?.click()} className="w-full h-28 rounded-xl border-2 border-dashed border-rose-200 bg-rose-50/40 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-rose-50 hover:border-rose-300 transition-all">
                    <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-rose-100 text-rose-400"><i className="ri-upload-cloud-2-line text-lg"></i></div>
                    <p className="text-xs text-gray-500 font-semibold">לחץ להעלאת תמונה</p>
                  </div>
                ) : (
                  <div className="relative w-full h-28 rounded-xl overflow-hidden border-2 border-rose-100 group">
                    <img src={form.image} alt="" className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-2">
                      <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-1.5 rounded-xl bg-white text-gray-700 text-xs font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"><i className="ri-refresh-line text-sm"></i> החלף</button>
                      <button type="button" onClick={() => { setForm((f) => ({ ...f, image: '' })); setPreview(''); if (fileRef.current) fileRef.current.value = ''; }} className="px-3 py-1.5 rounded-xl bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap"><i className="ri-delete-bin-line text-sm"></i> הסר</button>
                    </div>
                  </div>
                )}
                {uploading && <p className="text-xs text-rose-500 mt-1">מעלה תמונה...</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-semibold text-gray-600 mb-1">צבע גבול</label><input type="color" value={form.border_color} onChange={(e) => setForm((f) => ({ ...f, border_color: e.target.value }))} className="w-full h-10 rounded-xl border-2 border-rose-100 cursor-pointer" /></div>
                <div><label className="block text-xs font-semibold text-gray-600 mb-1">חדש?</label><div className="flex gap-2 mt-2">{[true, false].map((v) => (<button key={String(v)} type="button" onClick={() => setForm((f) => ({ ...f, is_new: v }))} className={`flex-1 py-2 rounded-xl border-2 text-xs font-semibold cursor-pointer ${form.is_new === v ? 'bg-rose-400 text-white border-rose-400' : 'bg-white text-gray-400 border-gray-200'}`}>{v ? 'כן' : 'לא'}</button>))}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-semibold text-gray-600 mb-1">סוג קישור</label><select value={form.link_type} onChange={(e) => setForm((f) => ({ ...f, link_type: e.target.value }))} className={inputClass}><option value="">פופ-אפ</option><option value="scroll">גלילה</option><option value="route">ניווט</option></select></div>
                <div><label className="block text-xs font-semibold text-gray-600 mb-1">ערך קישור</label><input type="text" value={form.link_value} onChange={(e) => setForm((f) => ({ ...f, link_value: e.target.value }))} className={inputClass} placeholder="scroll id / route path" /></div>
              </div>
              <div><label className="block text-xs font-semibold text-gray-600 mb-1">תוכן פופ-אפ</label><textarea rows={3} value={form.popup_content} onChange={(e) => setForm((f) => ({ ...f, popup_content: e.target.value }))} className={`${inputClass} resize-none`} /></div>
              <div><label className="block text-xs font-semibold text-gray-600 mb-1">סטטוס</label><div className="flex gap-2">{['visible', 'hidden'].map((v) => (<button key={v} type="button" onClick={() => setForm((f) => ({ ...f, visible: v === 'visible' }))} className={`flex-1 py-2 rounded-xl border-2 text-xs font-semibold cursor-pointer ${(v === 'visible' && form.visible) || (v === 'hidden' && !form.visible) ? 'bg-rose-400 text-white border-rose-400' : 'bg-white text-gray-400 border-gray-200'}`}>{v === 'visible' ? 'מוצג' : 'מוסתר'}</button>))}</div></div>
              <div className="flex gap-3 pt-2">
                <button onClick={handleSave} className="flex-1 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap">{editStory ? 'שמור שינויים' : 'הוסף סטורי'}</button>
                <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">ביטול</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4" dir="rtl">
          <div className="bg-white rounded-2xl border-2 border-rose-100 p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-red-50 border-2 border-red-100 mx-auto mb-4"><i className="ri-delete-bin-line text-2xl text-red-400"></i></div>
            <h3 className="font-bold text-gray-800 mb-2">מחיקת סטורי</h3>
            <p className="text-sm text-gray-500 mb-5">האם אתה בטוח?</p>
            <div className="flex gap-3">
              <button onClick={confirmDelete} className="flex-1 py-2.5 rounded-xl bg-red-400 text-white text-sm font-bold hover:bg-red-500 transition-all cursor-pointer whitespace-nowrap">מחק</button>
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">ביטול</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
